import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HeartHandshake, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const QUICK = [5, 10, 20];
const MAX = 25;
const REASONS = ['Missed load bonus', 'Game issue', 'Payment delay', 'Customer service'];

export const GoodwillCreditDialog = ({ player, onDone, trigger }) => {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const amt = parseFloat(amount);
    const valid = amt > 0 && amt <= MAX && reason.trim().length >= 3;

    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/staff/goodwill-credit`,
                { player_id: player.id, amount: amt, reason: reason.trim() }, { withCredentials: true });
            toast.success(data.message);
            setOpen(false); setAmount(''); setReason('');
            onDone?.(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Goodwill credit failed');
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-sm" data-testid="goodwill-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-pink-300">
                        <HeartHandshake className="w-5 h-5" /> Goodwill Free Play
                    </DialogTitle>
                    <DialogDescription className="text-white/60">
                        For {player?.name || 'this player'} · up to ${MAX} · no wagering needed
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-3 gap-2">
                    {QUICK.map((q) => (
                        <Button key={q} type="button" variant="outline" onClick={() => setAmount(String(q))}
                            className={`border-white/15 ${amt === q ? 'bg-pink-500/20 text-pink-200 border-pink-400/50' : 'text-white'}`}
                            data-testid={`goodwill-quick-${q}`}>${q}</Button>
                    ))}
                </div>
                <Input type="number" min="1" max={MAX} step="1" value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder={`Custom amount (max $${MAX})`} className="bg-black/40 border-white/15 text-white"
                    data-testid="goodwill-amount" />
                {amt > MAX && <p className="text-xs text-red-400" data-testid="goodwill-max-error">Max ${MAX} per credit</p>}
                <div className="flex flex-wrap gap-1.5">
                    {REASONS.map((r) => (
                        <button key={r} type="button" onClick={() => setReason(r)}
                            className={`text-[11px] px-2 py-1 rounded-full border ${reason === r ? 'border-pink-400 text-pink-200 bg-pink-500/15' : 'border-white/15 text-white/70'}`}
                            data-testid={`goodwill-reason-${r.toLowerCase().replace(/\s+/g, '-')}`}>{r}</button>
                    ))}
                </div>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)"
                    className="bg-black/40 border-white/15 text-white" data-testid="goodwill-reason" />
                <DialogFooter>
                    <Button onClick={submit} disabled={!valid || busy}
                        className="w-full bg-pink-500/25 hover:bg-pink-500/35 text-pink-100 border border-pink-400/40"
                        data-testid="goodwill-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Give ${amt > 0 ? `$${amt.toFixed(2)}` : ''} Free Play`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default GoodwillCreditDialog;
